import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  isValidHeaderImageHeight,
  MAX_HEADER_IMAGE_HEIGHT,
  MIN_HEADER_IMAGE_HEIGHT,
} from 'admin-panel/utils/page-header-image';
import { TEXT_FIELD_COPY_TASK_TIMEOUT } from 'admin-panel/constants/ec-timeouts';
import { didCancel, task, timeout } from 'ember-concurrency';

import type FlashMessageService from 'ember-cli-flash/services/flash-messages';
import type IntlService from 'ember-intl/services/intl';

interface PanelCategoryPageHeaderImageOptionsModalSignature {
  Args: {
    height: number | null;
    imageUrl: string;
    canShare?: boolean;
    isShared?: boolean;
    hasPendingChanges?: boolean;
    isRemovalPending?: boolean;
    onClose: () => void;
    onSave: (height: number | null, isShared: boolean) => boolean;
  };
}

export default class PanelCategoryPageHeaderImageOptionsModalComponent extends Component<PanelCategoryPageHeaderImageOptionsModalSignature> {
  @service
  declare flashMessages: FlashMessageService;

  @service
  declare intl: IntlService;

  @tracked
  heightValue: string;

  @tracked
  heightTouched = false;

  @tracked
  isShared: boolean;

  initialHeight: string;

  constructor(
    owner: unknown,
    args: PanelCategoryPageHeaderImageOptionsModalSignature['Args']
  ) {
    super(owner, args);
    // The field displays whole pixels, so compatible fractional values are
    // deliberately rounded into an immediately saveable normalization draft.
    this.initialHeight =
      args.height === null ? '' : String(Math.round(args.height));
    this.heightValue = this.initialHeight;
    this.isShared = Boolean(args.isShared);
  }

  get parsedHeight() {
    return this.heightValue === '' ? null : Number(this.heightValue);
  }

  get heightError() {
    const height = this.parsedHeight;

    return height === null || !isValidHeaderImageHeight(height)
      ? this.intl.t(
          'panels.category-list.page-editor.header-image.options.height-range',
          {
            min: MIN_HEADER_IMAGE_HEIGHT,
            max: MAX_HEADER_IMAGE_HEIGHT,
          }
        )
      : null;
  }

  get isHeightEditable() {
    return !this.args.isRemovalPending && this.args.height !== null;
  }

  get heightErrors() {
    return this.heightError ? [this.heightError] : [];
  }

  get canAttemptSave() {
    return (
      this.isShared !== Boolean(this.args.isShared) ||
      this.args.hasPendingChanges ||
      (this.isHeightEditable &&
        (this.parsedHeight !== this.args.height || this.heightError !== null))
    );
  }

  @action
  updateHeight(value: string) {
    this.heightValue = value;
  }

  @action
  updateSharing(isShared: boolean) {
    this.isShared = isShared;
  }

  @action
  save(closeModal: () => void) {
    const height = this.parsedHeight;
    this.heightTouched = true;

    if (
      !this.canAttemptSave ||
      (this.isHeightEditable && (this.heightError || height === null))
    ) {
      return;
    }

    if (this.args.onSave(height, this.isShared)) closeModal();
  }

  copyImageUrlTask = task({ drop: true }, async () => {
    try {
      await timeout(TEXT_FIELD_COPY_TASK_TIMEOUT);
      await navigator.clipboard.writeText(this.args.imageUrl);
      this.flashMessages.success(
        this.intl.t(
          'panels.category-list.page-editor.header-image.options.copy-success'
        )
      );
    } catch (error) {
      if (didCancel(error)) return;

      console.error('Failed to copy the header image URL', error);
      this.flashMessages.danger(
        this.intl.t(
          'panels.category-list.page-editor.header-image.options.copy-failed'
        )
      );
    }
  });
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Panel::Category::PageHeaderImageOptionsModal': typeof PanelCategoryPageHeaderImageOptionsModalComponent;
    'panel/category/page-header-image-options-modal': typeof PanelCategoryPageHeaderImageOptionsModalComponent;
  }
}
